import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getTournamentById,
  createTournament,
  updateTournament,
} from '../../api/wikiApi';
import {
  Trophy,
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  Users,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Swords,
} from 'lucide-react';
import styles from './WikiTournamentEditor.module.css';

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Черновик' },
  { value: 'registration', label: 'Регистрация открыта' },
  { value: 'ongoing', label: 'В процессе' },
  { value: 'finished', label: 'Завершён' },
  { value: 'cancelled', label: 'Отменён' },
];

const emptyReward = () => ({ text: '' });

const emptyPrize = (place) => ({
  place,
  rewards: [emptyReward()],
});

const emptyTournament = () => ({
  title: '',
  description: '',
  status: 'draft',
  startDate: '',
  prizes: [
    emptyPrize(1),
    emptyPrize(2),
    emptyPrize(3),
  ],
  participants: [],
  rounds: [],
});

// Миграция старого формата { amount, currency } → новый { rewards }
function migratePrizes(prizes) {
  if (!Array.isArray(prizes)) return [emptyPrize(1), emptyPrize(2), emptyPrize(3)];
  return prizes.map((p) => {
    if (Array.isArray(p.rewards)) return p;
    // старый формат
    const text = p.amount ? `${p.amount} ${p.currency || '₽'}`.trim() : '';
    return { place: p.place, rewards: [{ text }] };
  });
}

const emptyRound = (index) => ({
  id: Date.now() + index,
  name: `Раунд ${index + 1}`,
  matches: [],
});

const emptyMatch = () => ({
  id: Date.now() + Math.random(),
  player1: '',
  player2: '',
  score1: '',
  score2: '',
  winner: '',
});

// Генерация турнирной сетки из списка участников
function generateBracket(participants, existingRounds) {
  if (!participants || participants.length < 2) return existingRounds || [];

  const count = participants.length;
  // Ближайшая степень двойки
  const bracketSize = Math.pow(2, Math.ceil(Math.log2(count)));
  const roundCount = Math.log2(bracketSize);

  const rounds = [];
  for (let r = 0; r < roundCount; r++) {
    const matchCount = bracketSize / Math.pow(2, r + 1);
    const matches = [];
    for (let m = 0; m < matchCount; m++) {
      const existingMatch =
        existingRounds?.[r]?.matches?.[m] || null;
      if (r === 0) {
        const p1 = participants[m * 2] || '';
        const p2 = participants[m * 2 + 1] || '';
        matches.push({
          id: existingMatch?.id || Date.now() + r * 1000 + m,
          player1: existingMatch?.player1 ?? p1,
          player2: existingMatch?.player2 ?? p2,
          score1: existingMatch?.score1 ?? '',
          score2: existingMatch?.score2 ?? '',
          winner: existingMatch?.winner ?? '',
        });
      } else {
        matches.push({
          id: existingMatch?.id || Date.now() + r * 1000 + m,
          player1: existingMatch?.player1 ?? 'TBD',
          player2: existingMatch?.player2 ?? 'TBD',
          score1: existingMatch?.score1 ?? '',
          score2: existingMatch?.score2 ?? '',
          winner: existingMatch?.winner ?? '',
        });
      }
    }
    const existingRound = existingRounds?.[r];
    rounds.push({
      id: existingRound?.id || Date.now() + r,
      name: existingRound?.name || `Раунд ${r + 1}`,
      matches,
    });
  }
  return rounds;
}

const WikiTournamentEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const [form, setForm] = useState(emptyTournament());
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [participantsText, setParticipantsText] = useState('');
  const [collapsedRounds, setCollapsedRounds] = useState({});

  const loadTournament = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getTournamentById(id);
      if (!data) {
        setError('Турнир не найден');
        return;
      }
      setForm({
        title: data.title || '',
        description: data.description || '',
        status: data.status || 'draft',
        startDate: data.startDate
          ? data.startDate.slice(0, 16)
          : '',
        prizes: Array.isArray(data.prizes) && data.prizes.length > 0
          ? migratePrizes(data.prizes)
          : emptyTournament().prizes,
        participants: Array.isArray(data.participants) ? data.participants : [],
        rounds: Array.isArray(data.rounds) ? data.rounds : [],
      });
      setParticipantsText(
        Array.isArray(data.participants) ? data.participants.join('\n') : ''
      );
    } catch (err) {
      setError(err.message || 'Ошибка загрузки турнира');
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => {
    loadTournament();
  }, [loadTournament]);

  // Синхронизация участников из textarea
  const handleParticipantsChange = (text) => {
    setParticipantsText(text);
    const list = text
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    setForm((prev) => ({ ...prev, participants: list }));
  };

  // Генерация сетки
  const handleGenerateBracket = () => {
    const rounds = generateBracket(form.participants, form.rounds);
    setForm((prev) => ({ ...prev, rounds }));
  };

  // Добавить раунд вручную
  const handleAddRound = () => {
    setForm((prev) => ({
      ...prev,
      rounds: [...prev.rounds, emptyRound(prev.rounds.length)],
    }));
  };

  // Удалить раунд
  const handleDeleteRound = (roundIdx) => {
    setForm((prev) => ({
      ...prev,
      rounds: prev.rounds.filter((_, i) => i !== roundIdx),
    }));
  };

  // Изменить поле раунда
  const handleRoundChange = (roundIdx, field, value) => {
    setForm((prev) => {
      const rounds = prev.rounds.map((r, i) =>
        i === roundIdx ? { ...r, [field]: value } : r
      );
      return { ...prev, rounds };
    });
  };

  // Добавить матч в раунд
  const handleAddMatch = (roundIdx) => {
    setForm((prev) => {
      const rounds = prev.rounds.map((r, i) =>
        i === roundIdx
          ? { ...r, matches: [...(r.matches || []), emptyMatch()] }
          : r
      );
      return { ...prev, rounds };
    });
  };

  // Удалить матч
  const handleDeleteMatch = (roundIdx, matchIdx) => {
    setForm((prev) => {
      const rounds = prev.rounds.map((r, i) =>
        i === roundIdx
          ? { ...r, matches: r.matches.filter((_, mi) => mi !== matchIdx) }
          : r
      );
      return { ...prev, rounds };
    });
  };

  // Изменить поле матча
  const handleMatchChange = (roundIdx, matchIdx, field, value) => {
    setForm((prev) => {
      const rounds = prev.rounds.map((r, i) => {
        if (i !== roundIdx) return r;
        const matches = r.matches.map((m, mi) =>
          mi === matchIdx ? { ...m, [field]: value } : m
        );
        return { ...r, matches };
      });
      return { ...prev, rounds };
    });
  };

  // ——— Призы ———
  const handleAddPrize = () => {
    setForm((prev) => ({
      ...prev,
      prizes: [...prev.prizes, emptyPrize(prev.prizes.length + 1)],
    }));
  };

  const handleDeletePrize = (idx) => {
    setForm((prev) => ({
      ...prev,
      prizes: prev.prizes.filter((_, i) => i !== idx),
    }));
  };

  // Изменить текст конкретной награды внутри места
  const handleRewardChange = (prizeIdx, rewardIdx, value) => {
    setForm((prev) => {
      const prizes = prev.prizes.map((p, pi) => {
        if (pi !== prizeIdx) return p;
        const rewards = p.rewards.map((r, ri) =>
          ri === rewardIdx ? { ...r, text: value } : r
        );
        return { ...p, rewards };
      });
      return { ...prev, prizes };
    });
  };

  // Добавить награду к месту
  const handleAddReward = (prizeIdx) => {
    setForm((prev) => {
      const prizes = prev.prizes.map((p, pi) =>
        pi === prizeIdx
          ? { ...p, rewards: [...p.rewards, emptyReward()] }
          : p
      );
      return { ...prev, prizes };
    });
  };

  // Удалить награду из места
  const handleDeleteReward = (prizeIdx, rewardIdx) => {
    setForm((prev) => {
      const prizes = prev.prizes.map((p, pi) => {
        if (pi !== prizeIdx) return p;
        const rewards = p.rewards.filter((_, ri) => ri !== rewardIdx);
        return { ...p, rewards: rewards.length > 0 ? rewards : [emptyReward()] };
      });
      return { ...prev, prizes };
    });
  };

  const toggleRound = (roundIdx) => {
    setCollapsedRounds((prev) => ({
      ...prev,
      [roundIdx]: !prev[roundIdx],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      setError('Введите название турнира');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const payload = {
        ...form,
        startDate: form.startDate || null,
        prizes: form.prizes.map((p) => ({
          ...p,
          rewards: (p.rewards || []).filter((r) => r.text.trim() !== ''),
        })).filter((p) => p.rewards.length > 0),
      };
      if (isNew) {
        const created = await createTournament(payload);
        setSuccess(true);
        setTimeout(() => navigate(`/wiki/admin/tournaments/${created.id}`), 800);
      } else {
        await updateTournament(id, payload);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 2000);
      }
    } catch (err) {
      setError(err.message || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingState}>
        <RefreshCw size={32} className={styles.spinning} />
        <span>Загрузка турнира...</span>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.pageHeader}>
        <button
          type="button"
          className={styles.backButton}
          onClick={() => navigate('/wiki/admin/tournaments')}
        >
          <ArrowLeft size={18} />
          Назад
        </button>
        <div className={styles.pageHeaderCenter}>
          <Trophy size={24} className={styles.pageHeaderIcon} />
          <h1 className={styles.pageTitle}>
            {isNew ? 'Новый турнир' : 'Редактирование турнира'}
          </h1>
        </div>
        <button
          type="button"
          className={styles.saveButton}
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? (
            <RefreshCw size={18} className={styles.spinning} />
          ) : (
            <Save size={18} />
          )}
          {saving ? 'Сохранение...' : 'Сохранить'}
        </button>
      </div>

      {error && (
        <div className={styles.errorBanner}>
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {success && (
        <div className={styles.successBanner}>
          ✓ Турнир успешно сохранён
        </div>
      )}

      <form onSubmit={handleSubmit} className={styles.form}>
        {/* ——— Основная информация ——— */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Основная информация</h2>
          <div className={styles.fieldGrid}>
            <div className={styles.fieldFull}>
              <label className={styles.label}>Название турнира *</label>
              <input
                type="text"
                className={styles.input}
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Например: Money Rain V2 PVP"
              />
            </div>
            <div className={styles.fieldFull}>
              <label className={styles.label}>Описание</label>
              <textarea
                className={styles.textarea}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Описание турнира, правила регистрации..."
                rows={3}
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Статус</label>
              <select
                className={styles.select}
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Дата и время начала</label>
              <input
                type="datetime-local"
                className={styles.input}
                value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
              />
            </div>
          </div>
        </section>

        {/* ——— Призовой фонд ——— */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Призовой фонд</h2>
            <button
              type="button"
              className={styles.addSmallButton}
              onClick={handleAddPrize}
            >
              <Plus size={14} />
              Добавить место
            </button>
          </div>
          <div className={styles.prizesGrid}>
            {form.prizes.map((prize, prizeIdx) => (
              <div key={prizeIdx} className={styles.prizeCard}>
                <div className={styles.prizePlaceRow}>
                  <div className={styles.prizePlace}>
                    {prizeIdx === 0 ? '🥇' : prizeIdx === 1 ? '🥈' : prizeIdx === 2 ? '🥉' : `#${prizeIdx + 1}`}
                    <span>{prize.place} место</span>
                  </div>
                  <button
                    type="button"
                    className={styles.deletePrizeButton}
                    onClick={() => handleDeletePrize(prizeIdx)}
                    title="Удалить место"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className={styles.rewardsList}>
                  {(prize.rewards || []).map((reward, rewardIdx) => (
                    <div key={rewardIdx} className={styles.rewardRow}>
                      <input
                        type="text"
                        className={styles.rewardInput}
                        value={reward.text}
                        onChange={(e) => handleRewardChange(prizeIdx, rewardIdx, e.target.value)}
                        placeholder="Например: 1500 ₽ или 200 Доблестных знаков"
                      />
                      {(prize.rewards || []).length > 1 && (
                        <button
                          type="button"
                          className={styles.deleteRewardButton}
                          onClick={() => handleDeleteReward(prizeIdx, rewardIdx)}
                          title="Удалить награду"
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  className={styles.addRewardButton}
                  onClick={() => handleAddReward(prizeIdx)}
                >
                  <Plus size={13} />
                  Добавить награду
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* ——— Участники ——— */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              <Users size={18} />
              Участники ({form.participants.length})
            </h2>
            <button
              type="button"
              className={styles.generateButton}
              onClick={handleGenerateBracket}
              disabled={form.participants.length < 2}
              title={form.participants.length < 2 ? 'Нужно минимум 2 участника' : 'Сгенерировать турнирную сетку'}
            >
              <Swords size={15} />
              Сгенерировать сетку
            </button>
          </div>
          <div className={styles.participantsHint}>
            Введите никнеймы участников — по одному на строку
          </div>
          <textarea
            className={styles.participantsTextarea}
            value={participantsText}
            onChange={(e) => handleParticipantsChange(e.target.value)}
            placeholder={'Игрок1\nИгрок2\nИгрок3\n...'}
            rows={8}
            spellCheck={false}
          />
          {form.participants.length > 0 && (
            <div className={styles.participantsTags}>
              {form.participants.map((p, i) => (
                <span key={i} className={styles.participantTag}>{p}</span>
              ))}
            </div>
          )}
        </section>

        {/* ——— Раунды / Турнирная сетка ——— */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              <Swords size={18} />
              Турнирная сетка ({form.rounds.length} раундов)
            </h2>
            <button
              type="button"
              className={styles.addSmallButton}
              onClick={handleAddRound}
            >
              <Plus size={14} />
              Добавить раунд
            </button>
          </div>

          {form.rounds.length === 0 ? (
            <div className={styles.emptyRounds}>
              <Swords size={40} className={styles.emptyRoundsIcon} />
              <p>Раундов пока нет. Добавьте участников и нажмите «Сгенерировать сетку» или добавьте раунды вручную.</p>
            </div>
          ) : (
            <div className={styles.roundsList}>
              {form.rounds.map((round, roundIdx) => (
                <div key={round.id || roundIdx} className={styles.roundCard}>
                  <div
                    className={styles.roundHeader}
                    onClick={() => toggleRound(roundIdx)}
                  >
                    <div className={styles.roundHeaderLeft}>
                      <span className={styles.roundNumber}>Раунд {roundIdx + 1}</span>
                      <input
                        type="text"
                        className={styles.roundNameInput}
                        value={round.name}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleRoundChange(roundIdx, 'name', e.target.value);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        placeholder="Название раунда"
                      />
                      <span className={styles.matchCount}>
                        {(round.matches || []).length} матчей
                      </span>
                    </div>
                    <div className={styles.roundHeaderRight}>
                      <button
                        type="button"
                        className={styles.deleteRoundButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteRound(roundIdx);
                        }}
                        title="Удалить раунд"
                      >
                        <Trash2 size={15} />
                      </button>
                      {collapsedRounds[roundIdx] ? (
                        <ChevronDown size={18} />
                      ) : (
                        <ChevronUp size={18} />
                      )}
                    </div>
                  </div>

                  {!collapsedRounds[roundIdx] && (
                    <div className={styles.roundBody}>
                      {(round.matches || []).length === 0 ? (
                        <div className={styles.emptyMatches}>Матчей нет</div>
                      ) : (
                        <div className={styles.matchesList}>
                          {(round.matches || []).map((match, matchIdx) => (
                            <div key={match.id || matchIdx} className={styles.matchRow}>
                              <span className={styles.matchIndex}>#{matchIdx + 1}</span>
                              <div className={styles.matchPlayers}>
                                <input
                                  type="text"
                                  className={`${styles.playerInput} ${match.winner === match.player1 && match.player1 ? styles.playerWinner : ''}`}
                                  value={match.player1}
                                  onChange={(e) =>
                                    handleMatchChange(roundIdx, matchIdx, 'player1', e.target.value)
                                  }
                                  placeholder="Игрок 1"
                                />
                                <div className={styles.matchVs}>
                                  <input
                                    type="text"
                                    className={styles.scoreInput}
                                    value={match.score1}
                                    onChange={(e) =>
                                      handleMatchChange(roundIdx, matchIdx, 'score1', e.target.value)
                                    }
                                    placeholder="—"
                                    maxLength={4}
                                  />
                                  <span className={styles.vsText}>:</span>
                                  <input
                                    type="text"
                                    className={styles.scoreInput}
                                    value={match.score2}
                                    onChange={(e) =>
                                      handleMatchChange(roundIdx, matchIdx, 'score2', e.target.value)
                                    }
                                    placeholder="—"
                                    maxLength={4}
                                  />
                                </div>
                                <input
                                  type="text"
                                  className={`${styles.playerInput} ${match.winner === match.player2 && match.player2 ? styles.playerWinner : ''}`}
                                  value={match.player2}
                                  onChange={(e) =>
                                    handleMatchChange(roundIdx, matchIdx, 'player2', e.target.value)
                                  }
                                  placeholder="Игрок 2"
                                />
                              </div>
                              <div className={styles.matchWinner}>
                                <label className={styles.winnerLabel}>Победитель:</label>
                                <select
                                  className={styles.winnerSelect}
                                  value={match.winner}
                                  onChange={(e) =>
                                    handleMatchChange(roundIdx, matchIdx, 'winner', e.target.value)
                                  }
                                >
                                  <option value="">—</option>
                                  {match.player1 && match.player1 !== 'TBD' && (
                                    <option value={match.player1}>{match.player1}</option>
                                  )}
                                  {match.player2 && match.player2 !== 'TBD' && (
                                    <option value={match.player2}>{match.player2}</option>
                                  )}
                                </select>
                              </div>
                              <button
                                type="button"
                                className={styles.deleteMatchButton}
                                onClick={() => handleDeleteMatch(roundIdx, matchIdx)}
                                title="Удалить матч"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <button
                        type="button"
                        className={styles.addMatchButton}
                        onClick={() => handleAddMatch(roundIdx)}
                      >
                        <Plus size={14} />
                        Добавить матч
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ——— Кнопка сохранения внизу ——— */}
        <div className={styles.formFooter}>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={() => navigate('/wiki/admin/tournaments')}
          >
            Отмена
          </button>
          <button
            type="submit"
            className={styles.saveButton}
            disabled={saving}
          >
            {saving ? (
              <RefreshCw size={18} className={styles.spinning} />
            ) : (
              <Save size={18} />
            )}
            {saving ? 'Сохранение...' : 'Сохранить турнир'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default WikiTournamentEditor;
